/**
 * FallDetail — die rechte Spalte von Konzept B (Inhalt von A).
 *
 * @description
 *   Kopf (Name · Kennung · Stufe · ⋮), Produktvorschau, die Checkliste der
 *   Schritte und der EINE Hauptknopf des ersten offenen Schritts. Der Rabatt
 *   steht offen im Schritt (kein Dialog mehr). Ein blockierter erster Schritt
 *   trägt Grund und Weg, aber KEINEN Knopf. Ein 409 `a_preis_geaendert` beim
 *   Freigeben öffnet eine Bestätigung, die den neuen Preis im Hausformat NENNT
 *   und genau EINEN Hauptknopf trägt.
 *
 * @useWhen
 *   - Der Vorschlagsteil zeigt den gewählten Fall
 *
 * @dontUseFor
 *   - Die Liste links (`FallListe`)
 *   - Eine Tabelle der offenen Fälle — die ersetzt dieser Umbau
 *
 * @related
 *   - `Vorschlagsteil.jsx`, `FallListe.jsx`, `SchrittListe.jsx`, `ProduktVorschau.jsx`
 *   - `schritte.js` — die Ableitung
 *
 * @props
 *   zeile          Die Fallzeile
 *   vorschau       Antwort von `GET /api/second-life/vorschau` oder null
 *   vorschauFehler Ein Satz, wenn die Vorschau nicht lesbar war
 *   vorschauLaedt  true, solange die Vorschau-Antwort aussteht (R16-C3)
 *   onVorschauNeu  () => … — die Vorschau erneut lesen (R16-C6)
 *   mobil          true = schmale Ansicht: „‹" steht in der Kopfzeile (R16-C2/C7)
 *   onZurueck      () => … — die mobile Rückkehr zur Liste
 *   fokus          true = den Fokus auf die Detailüberschrift setzen (R16-C4)
 *   onAnlegen      (zeile) => Promise — Shopify-Entwurf anlegen
 *   onFreigeben    (zeile, {bestaetigt}) => Promise — veröffentlichen
 *   onAblehnen     (zeile) => Promise — Verwerfen-Rückfrage beim Aufrufer;
 *                  `resolve` = abgebrochen/abgelegt, `reject` = Fehler ins Detail (R16-C1)
 *   onRabatt       (zeile, text) => … — nach Save/Suggest again
 *   onKladde       async (zeile) => id | null
 *   laufend        { [sku]: art }
 *   onZumEditor    (zeile) => … — zum Editor der Zeile in der Bestandstabelle
 */
import { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Link, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import RowActionsMenu from '../../components/common/RowActionsMenu';
import { SP, TOUCH_TARGET_COARSE, TOUCH_TARGET_COARSE_QUADRAT, PANEL_RADIUS } from '../../theme/tokens';
import {
  STUFE_TON, ZUSTAND_TON, ZUSTAND_LABEL, hatShopifyProdukt, shopifyProduktUrl,
  laeuftAn, laufArt, fortschrittText, alterText, euroText,
} from './bestand';
import Pill from '../../components/common/Pill';
import RabattBlock from './RabattBlock';
import SchrittListe from './SchrittListe';
import ProduktVorschau from './ProduktVorschau';
import { schritte } from './schritte';
import { serverSatz } from './fehler';

export default function FallDetail({
  zeile, vorschau = null, vorschauFehler = null, vorschauLaedt = false,
  onVorschauNeu, mobil = false, onZurueck, fokus = false,
  onAnlegen, onFreigeben, onAblehnen, onRabatt, onKladde,
  laufend = null, onZumEditor,
}) {
  const [rabattOffen, setRabattOffen] = useState(false);
  const [bestaetigung, setBestaetigung] = useState(null);
  const [fehler, setFehler] = useState(null);
  // 🔴 **Doppelklick-Riegel** (R15-B5): `laeuft` kommt vom Aufrufer und wird
  // erst nach dem naechsten Render wahr. Zwei Klicks im selben Tick saehen ihn
  // beide falsch — dieser Zustand haelt den ersten fest, bevor React rendert.
  // 🔴 **Je ZEILE, nicht je Detail** (A/B-Befund): zwei Zeilen duerfen
  // gleichzeitig arbeiten; der Riegel gilt nur fuer die, deren Klick laeuft.
  const [sendetSku, setSendetSku] = useState(null);
  const ausloeserRef = useRef(null);
  const titelRef = useRef(null);
  // 🔴 **Der Fall, der gerade im Detail steht** (Nachbesserung 3). Ein Fehler
  // kommt aus einer Zusage, die NACH dem Klick eintrifft — wechselt der Nutzer
  // in der Zwischenzeit den Fall, darf er nicht am NEUEN stehen. Der Ref traegt
  // den aktuellen Schluessel; `setFehlerFuer` vergleicht damit.
  const skuRef = useRef(zeile?.sku);

  // Beim Wechsel des Falls alles zurücksetzen.
  useEffect(() => {
    skuRef.current = zeile?.sku;
    setRabattOffen(false);
    setBestaetigung(null);
    setFehler(null);
  }, [zeile?.sku]);

  /**
   * Einen Fehler NUR setzen, wenn der Fall noch DERSELBE ist.
   *
   * 🔴 **Der Befund (Nachbesserung 3):** die Zusage einer Aktion traegt keine
   * Kennung. Waehlte der Nutzer waehrend eines laufenden Anlegens eine andere
   * Zeile, setzte die Absage der ALTEN Zeile den Fehler am NEUEN Fall — ein
   * Satz ueber einen Vorgang, den es hier nicht gibt. Der Reset-Effekt oben
   * leert den Fehler beim Wechsel; ohne diesen Vergleich schriebe die spaete
   * Zusage ihn danach wieder hin.
   */
  const setFehlerFuer = (sku, text) => {
    if (skuRef.current === sku) setFehler(text);
  };

  // 🔴 **R16-C4:** beim Öffnen des Details den Fokus auf die Überschrift —
  // mobil unmountet die Liste, und ohne diesen Schritt fiele der Fokus auf
  // `<body>`. Die Gegenrichtung (zurück auf die Zeile) macht der Aufrufer.
  useEffect(() => {
    if (fokus) titelRef.current?.focus?.();
  }, [fokus]);

  if (!zeile) {
    return (
      <Box data-testid="fall-detail" sx={{ p: SP.card }}>
        <Typography variant="body2" color="text.secondary">
          Select a case from the list.
        </Typography>
      </Box>
    );
  }

  const laeuft = laeuftAn(laufend, zeile);
  const gesperrt = laeuft || sendetSku === zeile.sku;
  const fortschritt = fortschrittText(laufArt(laufend, zeile));
  const steps = schritte(zeile, vorschau);
  // 🔴 **Das Alter steht im DETAILKOPF** (B.30, S5-Nachzug): die Zeile der Liste
  // traegt es nicht mehr (Konzept B, §3.1 nennt Name · Kennung · Stufe · ⋮), und
  // „wie lange wartet das schon" ist auf einem Arbeitsvorrat die erste Frage.
  // Ohne Datum steht NICHTS — nie ein erfundenes „today" (`alterText`).
  const alter = alterText(zeile.erfasst_am);

  const handleFreigeben = async (bestaetigt) => {
    if (sendetSku === zeile.sku) return;
    const sku = zeile.sku;
    setSendetSku(sku);
    setFehler(null);
    try {
      await onFreigeben?.(zeile, { bestaetigt });
    } catch (err) {
      const d = err?.response?.data;
      if (d?.error === 'a_preis_geaendert') {
        if (skuRef.current === sku) setBestaetigung({ alt: d.alt, neu: d.neu });
      } else {
        // 🔴 **Der Satz kommt aus `fehler.js`** (R16-W5): ein Code wie
        // `kein_rabatt` hat dort seinen eigenen Satz. Vorher stand hier der
        // allgemeine — und derselbe Fehler stand ZUSAETZLICH im Seitenkasten.
        setFehlerFuer(sku, serverSatz(err, 'The action did not go through. Please try again.'));
      }
    } finally {
      setSendetSku((x) => (x === sku ? null : x));
    }
  };

  const handleAnlegen = async () => {
    if (sendetSku === zeile.sku) return;
    const sku = zeile.sku;
    setSendetSku(sku);
    setFehler(null);
    try {
      await onAnlegen?.(zeile);
    } catch (err) {
      setFehlerFuer(sku, serverSatz(err, 'The action did not go through. Please try again.'));
    } finally {
      setSendetSku((x) => (x === sku ? null : x));
    }
  };

  /**
   * Verwerfen — die Rueckfrage steht beim Aufrufer, das ERGEBNIS hier.
   *
   * 🔴 **R16-C1 (Nachbesserung 3):** `onAblehnen` gibt jetzt ein Promise zurueck
   * (`ablehnen` in `SecondLifeBestand`): `resolve` heisst „abgebrochen oder
   * abgelegt", `reject` traegt den Fehler. Er steht damit an DERSELBEN Stelle
   * und in DERSELBEN Form wie jeder andere Detail-Fehler — vorher lag er im
   * Seitenkasten ueber der Liste, auf schmalen Viewports unsichtbar.
   */
  const handleAblehnen = async () => {
    const sku = zeile.sku;
    setFehler(null);
    try {
      await onAblehnen?.(zeile);
    } catch (err) {
      setFehlerFuer(sku, serverSatz(err, 'The case could not be discarded. Please try again.'));
    }
  };

  /** Die Handlung des ERSTEN offenen Schritts. */
  const knopfFuer = (s) => {
    // 🔴 **Im Bestätigungszustand gibt es genau EINEN Hauptknopf** (R15-B5,
    // R16-B3): der Release-Knopf des offenen Schritts tritt zurück, sonst
    // stünden zwei gefüllte Knöpfe untereinander und der obere liefe erneut in
    // denselben 409.
    if (s.key === 'freigabe' && bestaetigung) return null;
    if (s.key === 'rabatt') {
      const ohneKladde = zeile.id == null;
      return (
        <Button
          size="small"
          variant="outlined"
          disabled={gesperrt}
          aria-busy={gesperrt || undefined}
          sx={{ ...TOUCH_TARGET_COARSE }}
          onClick={async () => {
            setFehler(null);
            if (ohneKladde) {
              try {
                const id = await onKladde?.(zeile);
                if (id == null) return;
              } catch (err) {
                // 🔴 **R16-W5:** der Fehler steht EINMAL, hier im Detail —
                // der Seitenkasten traegt seit R16-C1 gar keinen Fehler mehr.
                setFehlerFuer(zeile.sku, serverSatz(err, 'The row could not be prepared. Please try again.'));
                return;
              }
            }
            setRabattOffen(true);
          }}
        >
          {ohneKladde ? 'Prepare the case and set the discount' : 'Set the discount'}
        </Button>
      );
    }
    if (s.key === 'text') {
      return onZumEditor ? (
        <Button size="small" variant="outlined" sx={{ ...TOUCH_TARGET_COARSE }} onClick={() => onZumEditor(zeile)}>
          Edit the text in the stock table
        </Button>
      ) : null;
    }
    if (s.key === 'entwurf') {
      return (
        <Button
          size="small"
          variant="outlined"
          disabled={gesperrt}
          aria-busy={gesperrt || undefined}
          sx={{ ...TOUCH_TARGET_COARSE }}
          startIcon={fortschritt ? <CircularProgress size={20} color="inherit" /> : null}
          onClick={handleAnlegen}
        >
          {fortschritt || 'Create the Shopify draft'}
        </Button>
      );
    }
    if (s.key === 'freigabe') {
      return (
        <Button
          size="small"
          variant="contained"
          disabled={gesperrt}
          aria-busy={gesperrt || undefined}
          sx={{ ...TOUCH_TARGET_COARSE }}
          startIcon={fortschritt ? <CircularProgress size={20} color="inherit" /> : null}
          onClick={() => handleFreigeben(false)}
        >
          {fortschritt || 'Release'}
        </Button>
      );
    }
    return null;
  };

  /** Der Weg aus einer Blockade — ein echtes Bedienelement (R15-W1 / R16-B4). */
  const wegFuer = (s) => {
    if (s.weg !== 'base_product') return null;
    const ziel = zeile.basis || zeile.sku;
    return (
      <Link
        component={RouterLink}
        to={`/products/${encodeURIComponent(ziel || '')}`}
        variant="caption"
        underline="hover"
      >
        Open the base product
      </Link>
    );
  };

  const menue = [
    {
      key: 'shopify',
      label: hatShopifyProdukt(zeile) ? 'Open in Shopify' : 'Find in Shopify',
      onClick: () => window.open(shopifyProduktUrl(zeile), '_blank', 'noopener'),
    },
  ];
  if (zeile.id != null) {
    menue.push({
      key: 'ablehnen',
      label: 'Discard',
      danger: true,
      disabled: laeuft,
      onClick: handleAblehnen,
    });
  }

  return (
    <Box data-testid="fall-detail" data-sku={zeile.sku || undefined} sx={{ p: SP.card }}>
      {/* Kopf: mobil „‹ Name" in EINER Zeile, die Pillen darunter (Konzept B,
          R16-C2/C7). Der Name bricht um statt abzuschneiden — er ist die
          Kennung des Falls. */}
      <Stack direction="row" alignItems="center" spacing={SP.sm} sx={{ mb: SP.tight }}>
        {mobil && onZurueck && (
          <Button
            size="small"
            variant="text"
            aria-label="Back"
            onClick={onZurueck}
            // 🔴 **R16-C7:** die Zielflaeche ist mindestens 44 x 44 px — auf
            // grobem UND feinem Zeiger. `...TOUCH_TARGET_COARSE` allein setzt
            // die 44 nur unter `@media (pointer: coarse)`; `minWidth: 0` +
            // `px: 4` liessen den Knopf auf feinem Zeiger ~15 px breit.
            sx={{ minWidth: 44, minHeight: 44, px: 0, color: 'text.primary', ...TOUCH_TARGET_COARSE_QUADRAT }}
          >
            {/* 🔴 **R16-B1 (Nachbesserung 3):** hier stand ein ~10 px grosses
                graues „‹" — als Zurueck-Hinweis zu leise. Es ist jetzt das
                Pfeil-Icon des Hauses (`ArrowBackIcon`, wie `BackButton`,
                `ViewHeader`, `ContextDrawer`), 20 px (`fontSize="small"`) in
                der TEXTfarbe, nicht in `text.secondary`. Die Flaeche bleibt
                mindestens 44 x 44 auf jedem Zeiger, `aria-label="Back"` bleibt;
                ein zusaetzliches Wort braucht das klare Icon nicht. */}
            <ArrowBackIcon fontSize="small" />
          </Button>
        )}
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography
            ref={titelRef}
            tabIndex={-1}
            variant="h6"
            component="h3"
            title={zeile.name || undefined}
            sx={{ overflowWrap: 'anywhere', outline: 'none' }}
          >
            {zeile.name || 'no base product'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {zeile.sku}
            {alter ? ` · ${alter}` : ''}
          </Typography>
        </Box>
        <Box
          onClickCapture={(e) => {
            const knopf = e.target?.closest?.('button');
            if (knopf) ausloeserRef.current = knopf;
          }}
          sx={{ flexShrink: 0, '& .MuiIconButton-root': { ...TOUCH_TARGET_COARSE_QUADRAT } }}
        >
          <RowActionsMenu
            size="small"
            disabled={laeuft}
            label={`Actions for ${zeile.sku || zeile.name || 'this case'}`}
            actions={menue}
          />
        </Box>
      </Stack>
      <Stack direction="row" spacing={SP.chip} sx={{ mb: SP.tight, flexWrap: 'wrap' }}>
        <Pill tone={STUFE_TON[zeile.grade] || 'plain'}>{zeile.grade}</Pill>
        <Pill tone={ZUSTAND_TON[zeile.state] || 'plain'} nowrap>
          {ZUSTAND_LABEL[zeile.state] || zeile.state}
        </Pill>
      </Stack>

      <ProduktVorschau
        zeile={zeile}
        vorschau={vorschau}
        fehler={vorschauFehler}
        laedt={vorschauLaedt}
        onNeu={onVorschauNeu}
      />

      <Typography variant="subtitle2" sx={{ mt: SP.card, mb: SP.chip }}>
        Steps before release
      </Typography>
      <SchrittListe
        schritte={steps}
        idBasis="detail"
        knopfFuer={knopfFuer}
        wegFuer={wegFuer}
        gedaempft={vorschauLaedt}
        inhaltFuer={(s) => (s.key === 'rabatt' && rabattOffen ? (
          <RabattBlock
            zeile={zeile}
            busy={laeuft}
            onGeaendert={(z, text) => {
              setRabattOffen(false);
              onRabatt?.(z, text);
            }}
          />
        ) : null)}
      />

      {bestaetigung && (
        // 🔴 **Warnfläche mit `role="alert"`** (R16-C4): die Bestätigung ist die
        // letzte Frage vor einer Preisänderung im Shop — ein Screenreader muss
        // sie ansagen, und der Fokus gehört auf ihren ersten Knopf.
        <Box
          role="alert"
          sx={{ mt: SP.tight, p: SP.tight, border: 1, borderColor: 'divider', borderRadius: PANEL_RADIUS }}
          data-testid="bestaetigung-a-preis"
        >
          <Typography variant="body2" sx={{ mb: SP.chip }}>
            {`The A price is now ${euroText(bestaetigung.neu)} (was ${euroText(bestaetigung.alt)}). Releasing would change the strike price.`}
          </Typography>
          <Stack direction="row" spacing={SP.chip}>
            <Button
              size="small"
              variant="contained"
              autoFocus
              disabled={gesperrt}
              aria-busy={gesperrt || undefined}
              sx={{ ...TOUCH_TARGET_COARSE }}
              onClick={() => handleFreigeben(true)}
            >
              {`Confirm ${euroText(bestaetigung.neu)} and release`}
            </Button>
            <Button size="small" variant="text" sx={{ ...TOUCH_TARGET_COARSE }}
              onClick={() => setBestaetigung(null)}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {fehler && (
        // 🔴 **Die EINE Fehlerflaeche des Details** (R16-W5): Warnfarbe statt
        // grauer Caption, `role="alert"` damit sie angesagt wird, und
        // `data-testid` fuer die Probe. Die Seitenmeldung entfaellt fuer
        // Vorgaenge mit Detailbezug — sonst stuende derselbe Satz zweimal.
        <Box
          role="alert"
          data-testid="fall-fehler"
          sx={{ mt: SP.tight, color: 'warning.main' }}
        >
          <Typography variant="caption" sx={{ display: 'block', color: 'inherit' }}>
            {fehler}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
